/**
 * Observability (FE-01 NFR-FE01-08, architecture §6): Sentry errors with `release = VITE_APP_VERSION`
 * and Web Vitals, in staging and production only, personal data scrubbed (`sentry.ts`).
 *
 * The Sentry SDK and web-vitals load lazily after the first render, so no page (the pay page
 * included) pays for them on first load. Reports made before that (missing i18n keys, unknown enum
 * values) are queued; each key is reported once per session.
 */
import type { Env } from "./envSchema";

type Reporter = (message: string) => void;

let reporter: Reporter | null = null;
let enabled = true;
const pending: string[] = [];
const reported = new Set<string>();

/** True for the environments that report (local development never sends anything). */
export function shouldReport(env: Pick<Env, "env" | "sentryDsn">): boolean {
  return Boolean(env.sentryDsn) && env.env !== "local";
}

/** Report a warning once per `key` per session (missing i18n keys, unknown enum values). */
export function reportOnce(key: string, message: string): void {
  if (reported.has(key) || !enabled) return;
  reported.add(key);
  if (reporter) reporter(message);
  else if (pending.length < 50) pending.push(message);
}

/** Start Sentry + Web Vitals (lazy). Safe to call when not configured. */
export async function initObservability(env: Env): Promise<void> {
  if (!shouldReport(env)) {
    enabled = false;
    pending.length = 0;
    return;
  }
  const { startSentry } = await import("./sentry");
  reporter = await startSentry(env);
  for (const message of pending.splice(0)) reporter(message);
}

/** Test helper. */
export function resetObservabilityForTests(): void {
  reporter = null;
  enabled = true;
  pending.length = 0;
  reported.clear();
}
